import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { LMFormatUtilService, LMUrlUtilService } from '@services/utils/index';
import { LMSpecService } from '@services/lm-spec.service';
import { LMConfirmationMessageService, ConfirmationDialogResult } from '@modal/lm-confirmation-message.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMContextService } from '@services/lm-context.service';
import { LMContextUtilService, LMGlobalUtilService } from '@utils/index';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMModel } from '@form/model/lm-model';
import { LMRoamingUIProfileService, RoamingUIProfileResult, RoamingUIProfileResponse } from '@services/lm-roamingUIProfile.service';

export class LMMenuEvent implements LMFieldEvent {
  link: string;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, link: string) {
    this.eventType = 'MenuClickEvent';
    this.event = event;
    this.link = link;
  }
}

export type LMCardType = 'webapp' | 'menu' | 'registration' | 'signin' | 'signout';

@Component({
  selector:    'lm-simple-card',
  templateUrl: 'lm-simple-card.component.html',
  styleUrls: [ 'lm-simple-card.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ LMRoamingUIProfileService ],
})

export class LMSimpleCardComponent implements OnInit {
  @HostBinding('class.card') get card() {
    return this.showBackground;
  }
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Input() type: LMCardType = 'webapp'; // 1 = webapp link, 2 = menu link
  @Input() scale: string;
  @Input() link: string;
  @Input() showBackground = true;
  @Input() imageUrl: string;
  @Input() title: LMMessageRule;
  @Input() description: LMMessageRule;
  @Input() paragraphLayout: boolean;
  @Input() preferred: boolean;
  @Input() webAppName: string;
  @Input() isLink: string;
  @Input() isExternalLink: string;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() anchorClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() preferredMenuEvent: EventEmitter<any> = new EventEmitter<any>();

  public confirmationMsg: string;
  public setPreferred: String = Soho.Locale.translate('SetPreferred');
  public removePreferred: String = Soho.Locale.translate('RemovePreferred');
  public preferredMsg: String = Soho.Locale.translate('Preferred');
  public globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
  public isInAccessibilityMode: boolean;
  isMultiLineDescription: boolean;

  constructor(private lmSpecService: LMSpecService,
    public lmUrlUtilService: LMUrlUtilService,
    public lmContextService: LMContextService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmConfirmationMessageService: LMConfirmationMessageService,
    private lmLoggerService: LMLoggerService,
    private lmRoamingUIProfileService: LMRoamingUIProfileService,
    public  lmContextUtilService: LMContextUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    public lmFormatUtilService: LMFormatUtilService) {
  }

  ngOnInit() {
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
  }

  onClickEvent(event: Event) {
    if (this.isWebAppLink) {
      if (this.lmContextService.inMingleWidget()) {
        let url = this.link;
        url += '&' + jQuery.param(this.lmContextService.getContext().contextSessionKeys, true);
        this.lmContextUtilService.sendDrillbackMessage(url);
      } else {
        this.lmGlobalUtilService.navigateByUrl(this.link);
        this.anchorClicked.emit(this.title);
      }
    } else if (this.lmContextService.inMingleWidget() && this.type === 'menu') {
      const url = this.lmUrlUtilService.updateContextSessionKeyParams(this.link);
      this.lmContextUtilService.sendDrillbackMessage(url);
    } else {
      if (this.isLink === 'true') {
        if (this.type === 'registration') {
          this.lmGlobalUtilService.navigateByUrl(this.link);
        } else if (this.type === 'signin' || this.type === 'signout') {
          this.lmGlobalUtilService.navigate(this.link);
        } else {
          this.lmGlobalUtilService.navigateByUrl(this.link);
        }
      } else {
        this.fieldEvent.emit(new LMMenuEvent(event, this.link));
      }
    }
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  onPreferredMenuEvent(event: Event) {
    if (this.isPreferred) {
      this.confirmationMsg = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('RemovePreferredPage'), this.title);
      this.globalPersonalization.preferredWebApp = '';
      this.displayRemoveConfirmation();
    } else {
      this.confirmationMsg = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('DefaultLandingPage'), this.title);
      this.displayConfirmation(this.webAppName);
    }
  }

  public displayConfirmation(preferredWebApp: string) {
    this.lmConfirmationMessageService.promptConfirmationMessage({ message: this.confirmationMsg, okToAll: false })
      .subscribe(
        (result: any) => {
          if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
            this.lmLoggerService.log([ 'Message confirmed: ', this.confirmationMsg ]);
            this.lmRoamingUIProfileService.setPreferredWebApp(preferredWebApp)
              .subscribe(
                (results: RoamingUIProfileResponse) => {
                  if (results.status === RoamingUIProfileResult.COMPLETED) {
                    this.globalPersonalization.preferredWebApp = preferredWebApp;
                    this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.globalPersonalization);
                    this.lmGlobalUtilService.navigateByUrl(this.link);
                    this.anchorClicked.emit(this.title);
                  }
                });
          } else {
            this.lmLoggerService.log([ 'Message not confirmed: ', this.confirmationMsg ]);
          }
        }
      );
  }

  public displayRemoveConfirmation() {
    this.lmConfirmationMessageService.promptConfirmationMessage({ message: this.confirmationMsg, okToAll: false })
      .subscribe(
        (result: any) => {
          if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
            this.lmLoggerService.log([ 'Message confirmed: ', this.confirmationMsg ]);
            this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.globalPersonalization);
            this.preferredMenuEvent.emit({ preferredWebAppRemoved: true, webApp: this.webAppName });
          } else {
            this.lmLoggerService.log([ 'Message not confirmed: ', this.confirmationMsg ]);
          }
        }
      );
  }

  public get isParagraph(): boolean {
    return this.paragraphLayout;
  }

  public get isWebAppLink(): boolean {
    if (this.type === 'webapp') {
      return true;
    }
  }

  public get hasImage(): boolean {
    if (this.imageUrl !== '') {
      return true;
    }
  }

  public get isLargeScale(): boolean {
    if (this.scale !== '0') {
      return true;
    }
  }

  public get isPreferred(): boolean {
    return this.preferred;
  }

  public get smallMenuInDashboard(): boolean {
    if (this.model) {
      return this.model.formSpec.busFormSpec.implementsDashBoardView && this.scale === '0';
    }
    return false;
  }

  showToolTip(el: HTMLElement) {
    this.isMultiLineDescription = el.scrollHeight > el.clientHeight;
  }
}
